import { ClientFinancialProfileModel } from '../client-financial-profile/client-financial-profile.model';
import {
  HealthInsuranceStatus,
  HousingSituation,
  MiscellaneousProperty,
  MobilityStatus,
  Pet,
  ProfessionStatus,
  PropertyOwnership,
  ProvisionTarget,
  RelationshipStatus,
} from '../../enum';

export class ActivityTypePill {
  name: string;
  type: Array<string>;
  active: boolean;
}

export class ActivityModel {
  id: number;
  createdAt: Date;
  automaticActionTriggered: boolean;
  automaticActionEnabled: boolean;
  person: ShortPersonModel;
  logEvent:
    | ActivityInsignDocumentSignedEvent
    | ActivityChatAttachmentEvent
    | ActivityDocumentUploadedEvent
    | ActivityCardBankAccessEvent
    | ActivityBirthdayEvent
    | ActivityContractCreate
    | ActivityContractEdit
    | ActivityContractDelete
    | ActivityDamageReport
    | ActivityMandateEvent
    | ActivityPersonCreation
    | ActivityPersonEdit
    | ActivityFinanceCheckCreation
    | ActivityFinanceCheckEdit
    | ActivityRegisterEvent
    | ActivityReminderEvent
    | ActivityTerminationDetailEvent
    | ActivityLeadEvent
    | ActivityWealthArtModel
    | ActivityWealthPreciousMetalModel
    | ActivityWealthJewelryModel
    | ActivityWealthVehicleModel
    | ActivityWealthRealEstateModel
    | ActivityWealthOtherModel;
}

export class ShortPersonModel {
  id: number;
  keycloakClientId: string;
  firstName: string;
  lastName: string;
  profilePictureUrl: string;
  onboardingPending?: boolean;
}

export class ActivityCardBankAccessEvent {
  eventType: string;
  accounts: ActivityAccountModel[];
  isShared: boolean;
}

export class ActivityAccountModel {
  name: string;
  iban: string;
  bankIconUrl: string;
  accountHolderName: string;
  balance: number;
  type: string;
  id: string;
}

export class ActivityBirthdayEvent {
  eventType: string;
  firstName: string;
  lastName: string;
  description: string;
}

export class ActivityContractCreate {
  eventType: string;
  id: number;
  uid: string;
  title: string;
  description: string;
  createdDate: Date;
  iconUrl: string;
  insuranceProviderId: number;
  insuranceGroupId: number;
}

export class ActivityContractEdit {
  eventType: string;
  id: number;
  uid: string;
  title: string;
  description: string;
  editDate: Date;
  insuranceProviderId: number;
  insuranceGroupId: number;
}

export class ActivityContractDelete {
  eventType: string;
  id: number;
  title: string;
  description: string;
  editDate: Date;
  iconUrl: string;
  insuranceProviderId: number;
  insuranceGroupId: number;
}

export class ActivityDamageReport {
  eventType: string;
  contracUid: string;
  firstName: string;
  lastName: string;
  iconUrl: string;
  contractId: number;
  description: string;
  contractTitle: string;
  reportTitle: string;
}

export class ActivityMandateEvent {
  eventType: string;
  firstName: string;
  lastName: string;
  description: string;
  status: string;
}

export class ActivityPersonCreation {
  eventType: string;
  birthday: Date;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  mobile: string;
}

export class ActivityPersonEdit {
  eventType: string;
  birthday: Date;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  mobile: string;
}

export class ActivityFinanceCheckCreation {
  eventType: string;
  birthday: Date;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  description: string;
}

export class ActivityFinanceCheckEdit {
  eventType: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  description: string;
  updatedFields: ActivityFinanceCheckUpdatedFields;
}

export class ActivityFinanceCheckUpdatedFields {
  netIncome?: string; // e.g. "€222.0"
  relationshipStatus?: string;
  professionStatus?: string;
  healthInsuranceStatus?: string;
  childrenAmount?: string;
  childBenefit?: string;
  housingSituation?: string;
  mobilityStatuses: string;
  propertyOwnerships: string;
  provisionTargets: string;
  miscellaneousProperties: string;
  pets: string;
}

export class ActivityRegisterEvent {
  eventType: string;
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  birthday: Date;
  description: string;
  mobile: string;
}

export class ActivityReminderEvent {
  eventType: string;
  clientId: number;
  firstName: string;
  lastName: string;
  note: string;
  description: string;
}

export class ActivityTerminationDetailEvent {
  eventType: string;
  contractId: number;
  contractUid: string;
  title: string;
  createdDate: Date;
  insuranceProviderId: number;
  insuranceGroupId: number;
  iconUrl: string;
  description: string;
}

export class ActivityLeadEvent {
  eventType: string;
  leadCampaignName: string;
  createdDate: Date;
}

export class ActivityDocumentUploadedEvent {
  eventType: string;
  documentType: string;
  filename: string;
  createdAt: Date;
  documentUrl: string;
  assetId: string;
}

export class ActivityChatAttachmentEvent {
  assetId: string;
  attachmentUrl: string;
  eventType: string;
  filename: string;
}

export class ActivityInsignDocumentSignedEvent {
  clientEmail: string;
  documentName: string;
  eventType: string;
  firstName: string;
  lastName: string;
}

export class ActivityAdvisorsRequest {
  page: number = 1;
  perPage: number = 100;
  companyId: number;
  query: string;
}

export class ActivityAdvisorsResponse {
  hasMore: boolean;
  advisors: ShortAdvisorModel[];
}

export class ShortAdvisorModel {
  keycloakAdvisorId: string;
  uid: string;
  firstName: string;
  lastName: string;
  token: string;
  activitiesAmount: number;
  profilePictureUrl: string;
  selected?: boolean = true;
}

export class ActivitySavedFiltersModel {
  eventTypes: string[];
  keycloakAdvisorIds: string[];
}

export class ActivityWealthArtModel {
  eventType: string;
  id: number;
  category: string;
  subCategory: string;
  title: string;
  createdDate: Date;
  amount: number;
}

export class ActivityWealthOtherModel {
  eventType: string;
  id: number;
  category: string;
  subCategory: string;
  title: string;
  createdDate: Date;
  purchasePrice: number;
  purchaseDate: Date;
  currentMarketValue: number;
}

export class ActivityWealthPreciousMetalModel {
  eventType: string;
  id: number;
  category: string;
  subCategory: string;
  title: string;
  purchasePrice: number;
  purchaseDate: Date;
  currentMarketValue: number;
  createdDate: Date;
}

export class ActivityWealthJewelryModel {
  eventType: string;
  id: number;
  category: string;
  subCategory: string;
  title: string;
  currentMarketValue: number;
  createdDate: Date;
}

export class ActivityWealthVehicleModel {
  eventType: string;
  id: number;
  category: string;
  subCategory: string;
  manufacturerName: string;
  amount: number;
  createdDate: Date;
  model: string;
  mileage: number;
}

export class ActivityWealthRealEstateModel {
  eventType: string;
  id: number;
  category: string;
  subCategory: string;
  title: string;
  address: string;
  createdDate: Date;
  purchaseDate: Date;
  purchasePrice: number;
  propertySize: number;
  livingSpace: number;
  estimateCurrentValue: number;
}

// --- Automatic Actions ---

export class AutomaticActionLink {
  name: string = null;
  url: string = null;
}

export class AutomaticActionAttachment {
  id: number = null;
  filename: string = null;
  type: string = null;
  filesize: number = null;
  fileUrl: string = null;
  uploadUrl: string = null;
}

export class AutomaticActionAttachmentRequest {
  filename: string = null;
  type: string = null;
  filesize: number = null;
}

export class AutomaticActionModel {
  id: number = null;
  eventType: string = null;
  messageTemplate: string = null;
  enabled: boolean = null;
  videoId: number = null;
  link: AutomaticActionLink = null;
  attachments: AutomaticActionAttachment[] = [];
  createdAt: string = null;
  updatedAt: string = null;
}

export class CreateAutomaticActionRequest {
  eventType: string = null;
  messageTemplate: string = null;
  videoId: number = null;
  link: AutomaticActionLink = null;
  attachments: AutomaticActionAttachmentRequest[] = [];
}

export class UpdateAutomaticActionRequest {
  messageTemplate: string = null;
  videoId: number = null;
  link: AutomaticActionLink = null;
  attachments: AutomaticActionAttachmentRequest[] = [];
  // enabled: boolean = null;
}

export class TemplateVariablesResponse {
  eventType: string = null;
  variables: string[] = [];
}

export class ReferenceTemplateModel {
  id: number = null;
  eventType: string = null;
  messageTemplate: string = null;
  description: string = null;
}

export interface AutomaticActionDisplayConfig {
  label: string;
  badgeClass: string;
  icon: string;
}

// Keys match GET /activity/b2b/automatic-actions/event-types exactly (confirmed 2026-06 response).
// label values reused from ActivityComponent.activityTypePills where that type is already
// covered there — kept consistent with existing UI copy rather than inventing new wording.
export const AUTOMATIC_ACTION_DISPLAY_CONFIG: Record<string, AutomaticActionDisplayConfig> = {
  // Contracts
  NEW_CONTRACT: { label: 'Verträge (Erstellt)', badgeClass: 'badge-created', icon: 'bi-file-earmark-plus-fill' },
  EDIT_CONTRACT: { label: 'Verträge (Bearbeitet)', badgeClass: 'badge-created', icon: 'bi-file-earmark-text-fill' },
  // ARCHIVE_CONTRACT: { label: 'Verträge (Archiviert)', badgeClass: 'badge-archived', icon: 'bi-archive-fill' },
  DELETE_CONTRACT: { label: 'Verträge (Gelöscht)', badgeClass: 'badge-created', icon: 'bi-trash-fill' },

  // Person / client
  // NEW_PERSON: { label: 'Neuer Kunde', badgeClass: 'badge-created', icon: 'bi-person-plus-fill' },
  EDIT_PERSON_DETAIL: { label: 'Persönliches', badgeClass: 'badge-created', icon: 'bi-person-lines-fill' },
  CLIENT_COMPLETED_ONBOARDING: { label: 'Onboarding', badgeClass: 'badge-created', icon: 'bi-flag-fill' },

  // Mandate / finance profile
  CREATE_MANDATE: { label: 'Mandate', badgeClass: 'badge-created', icon: 'bi-briefcase-fill' },
  CREATE_FINANCE_CHECK: { label: 'Finanzprofil', badgeClass: 'badge-created', icon: 'bi-clipboard-data-fill' },

  // Damage / termination / reminders / registrations
  CREATE_DAMAGE_REPORT: {
    label: 'Schadensmeldungen',
    badgeClass: 'badge-created',
    icon: 'bi-exclamation-triangle-fill',
  },
  CREATE_REGISTER: { label: 'Registrierungen', badgeClass: 'badge-created', icon: 'bi-clipboard-check-fill' },
  CREATE_TERMINATION_DETAIL: { label: 'Kündigungsalarme', badgeClass: 'badge-created', icon: 'bi-bell-fill' },
  CREATE_REMINDER: { label: 'Wiedervorlagen', badgeClass: 'badge-created', icon: 'bi-alarm-fill' },

  // Birthday / leads
  CREATE_BIRTHDAY: { label: 'Geburtstag', badgeClass: 'badge-created', icon: 'bi-gift-fill' },
  // BUY_LEAD: { label: 'Leads', badgeClass: 'badge-created', icon: 'bi-star-fill' },

  // Financial analysis / bank
  SHARE_ACCOUNT: { label: 'Finanzanalysen', badgeClass: 'badge-created', icon: 'bi-graph-up' },
  ACCOUNT_BANK_CONNECTION: { label: 'Bankverbindung', badgeClass: 'badge-created', icon: 'bi-bank' },

  // Documents
  DOCUMENT_UPLOADED: { label: 'Persönliche Dokumente', badgeClass: 'badge-created', icon: 'bi-file-earmark-fill' },
  CHAT_ATTACHMENT_UPLOADED: { label: 'Chat Dokumente', badgeClass: 'badge-created', icon: 'bi-paperclip' },
  INSIGN_DOCUMENT_SIGNED: {
    label: 'inSign Dokumente',
    badgeClass: 'badge-completed',
    icon: 'bi-file-earmark-check-fill',
  },

  // Wealth
  CREATE_REAL_ESTATE_WEALTH: { label: 'Vermögen (Immobilie)', badgeClass: 'badge-created', icon: 'bi-house-fill' },
  CREATE_VEHICLE_WEALTH: { label: 'Vermögen (Fahrzeug)', badgeClass: 'badge-created', icon: 'bi-car-front-fill' },
  CREATE_JEWELRY_WEALTH: { label: 'Vermögen (Schmuck)', badgeClass: 'badge-created', icon: 'bi-gem' },
  CREATE_PRECIOUS_METAL_WEALTH: { label: 'Vermögen (Edelmetall)', badgeClass: 'badge-created', icon: 'bi-coin' },
  CREATE_ART_WEALTH: { label: 'Vermögen (Kunst)', badgeClass: 'badge-created', icon: 'bi-palette-fill' },
  CREATE_OTHER_WEALTH: { label: 'Vermögen (Sonstiges)', badgeClass: 'badge-created', icon: 'bi-box-seam-fill' },
};

export const DEFAULT_AUTOMATIC_ACTION_DISPLAY: AutomaticActionDisplayConfig = {
  label: 'Aktion',
  badgeClass: 'badge-default',
  icon: 'bi-chat-fill',
};
